import { useAppSelector } from "app/hooks";
import { RootState } from "app/store";
import { useAuth } from "hooks/auth";
import { querystring } from "models/Listing";
import { useSelector } from "react-redux";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import { AccessCheckFunction, AdminFormFields } from "types/general";
import { ListingProps } from "types/listing";
import { Access } from "types/role";
import { getEnvVariable } from "utilities/env";

export function useSettings() {
  const { code } = useAppSelector(selectCurrentStoreData);
  const { fetcher } = useAuth();

  const useTenantSettings = (selectedTenant?: string) => {
    const tenant = !!selectedTenant ? selectedTenant : code;
    const link = tenant
      ? `${getEnvVariable("REACT_APP_API_URL")}/${tenant}/settings`
      : "";
    return useSWRImmutable(link, fetcher);
  };

  const useCountries = () => {
    const link = `${getEnvVariable("REACT_APP_API_URL")}/settings/choices/country`;
    return useSWRImmutable(link, fetcher);
  };

  const useIdentificationMethods = () => {
    const link = `${getEnvVariable("REACT_APP_API_URL")}/settings/choices/identificationMethods`;
    return useSWRImmutable(link, fetcher);
  };

  const useTimezones = () => {
    const link = `${getEnvVariable("REACT_APP_API_URL")}/settings/choices/timezone`;
    return useSWRImmutable(link, fetcher);
  };

  const useCurrencies = () => {
    const link = `${getEnvVariable("REACT_APP_API_URL")}/settings/choices/currency`;
    return useSWRImmutable(link, fetcher);
  };

  const updateSettings = async (values: any, storeCode?: string) => {
    const apiStoreCode = storeCode ? storeCode : code;

    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${apiStoreCode}/settings`,
      {
        body: JSON.stringify({ settings: values }),
        headers: { "Content-Type": "application/json" },
        method: "PATCH",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const setLogo = async (file: any) => {
    const formData = new FormData();
    formData.append("photo[file]", file);
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/settings/logo`,
      { body: formData, method: "post" },
    );
    if (response.ok) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useAdminsList = (props?: ListingProps) => {
    return useSWR(
      () => `${getEnvVariable("REACT_APP_API_URL")}/admin${querystring(props)}`,
      fetcher,
    );
  };

  const enableNotifications = async (adminID: string, active: string) => {
    const response = await fetcher(
      `${getEnvVariable(
        "REACT_APP_API_URL",
      )}/${code}/admin/${adminID}/settings/notificationsEnabled`,
      {
        body: JSON.stringify({ value: active }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const addAdmin = async (
    values: AdminFormFields,
  ): Promise<{ data: { adminId: string } }> => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/admin/data`,
      {
        body: JSON.stringify({ admin: values }),
        headers: { "Content-Type": "application/json" },
        method: "post",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const changePassword = async (values: {
    currentPassword: string;
    plainPassword: string;
  }) => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/admin/password`,
      {
        body: JSON.stringify(values),
        headers: { "Content-Type": "application/json;charset=UTF-8" },
        method: "put",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const resetPassword = async (plainPassword: string, token: string) => {
    const response = await fetch(
      `${getEnvVariable("REACT_APP_API_URL")}/admin/password/reset`,
      {
        body: JSON.stringify({
          reset: { plainPassword: plainPassword },
          token: token,
        }),
        headers: { "Content-Type": "application/json;charset=UTF-8" },
        method: "post",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const requestPasswordChange = async (username: string) => {
    const response = await fetch(
      `${getEnvVariable("REACT_APP_API_URL")}/admin/password/reset/request`,
      {
        body: JSON.stringify({ username: username }),
        headers: { "Content-Type": "application/json;charset=UTF-8" },
        method: "post",
      },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  const useAdminData = (memberID?: string) => {
    return useSWRImmutable(
      `${getEnvVariable("REACT_APP_API_URL")}/admin/data${
        memberID ? `/${memberID}` : ""
      }`,
      fetcher,
    );
  };

  const editAdmin = async (
    adminID: string,
    values: AdminFormFields,
  ): Promise<{ data: { adminID: string } }> => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/admin/data/${adminID}`,
      {
        body: JSON.stringify({ admin: values }),
        headers: { "Content-Type": "application/json" },
        method: "put",
      },
    );
    const data = await response.json();
    if (response.ok) {
      return { data };
    } else {
      throw data;
    }
  };

  const removeLogo = async () => {
    const response = await fetcher(
      `${getEnvVariable("REACT_APP_API_URL")}/${code}/settings/logo`,
      { method: "delete" },
    );
    if (response.status === 204) {
      return true;
    } else {
      throw await response.json();
    }
  };

  return {
    addAdmin,
    changePassword,
    editAdmin,
    enableNotifications,
    removeLogo,
    requestPasswordChange,
    resetPassword,
    setLogo,
    updateSettings,
    useAdminData,
    useAdminsList,
    useTenantSettings,
    useCountries,
    useIdentificationMethods,
    useTimezones,
    useCurrencies,
  };
}

/**
 * Checks if the user has a specific level of access (VIEW or MODIFY) to a resource.
 * If no level is provided, checks for any access.
 */
export const HasAccessToResource: AccessCheckFunction = (resource, level) => {
  // Retrieves the access control list (ACL) from the Redux store's state
  const accessMap = useSelector((state: RootState) => state?.ACL);

  // If accessMap is undefined or resource doesn't exist in the map, return false
  if (!accessMap || !accessMap[resource]) {
    return false;
  }

  // If a level is specified, check if access level matches
  if (level) {
    return accessMap[resource] === level;
  }

  // If no specific level is specified, return true for any access
  return !!accessMap[resource];
};

/**
 * Checks if the user has full access ("MODIFY" access) to a given resource.
 */
export const hasModifyAccessToResource: AccessCheckFunction = (resource) =>
  HasAccessToResource(resource, Access.MODIFY);

/**
 * Checks if the user has restricted access ("VIEW"-only access) to a given resource.
 */
export const hasViewAccessToResource: AccessCheckFunction = (resource) =>
  HasAccessToResource(resource, Access.VIEW);

/**
 * Checks if the user has any level of access ("MODIFY" or "VIEW") to a given resource.
 */
export const hasAnyAccessToResource: AccessCheckFunction = (resource) =>
  HasAccessToResource(resource);
